import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchArtistDetails, fetchArtworksByArtist, fetchGalleries, updateArtworkLocation } from '../utils/api';
import SEO from './SEO';
import '../styles/ArtistDashboard.css';

const ArtistDetailPage = () => {
  const { artistId } = useParams();
  const [artist, setArtist] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingLocation, setUpdatingLocation] = useState(null);
  const [gallerySearchTerms, setGallerySearchTerms] = useState({});

  useEffect(() => {
    const loadArtistData = async () => {
      try {
        setLoading(true);
        const [artistDetails, artistArtworks, galleriesData] = await Promise.all([
          fetchArtistDetails(artistId),
          fetchArtworksByArtist(artistId),
          fetchGalleries()
        ]);
        setArtist(artistDetails);
        setArtworks(artistArtworks);
        setGalleries(galleriesData);
        
        // Initialize search terms for each artwork
        const initialSearchTerms = {};
        artistArtworks.forEach(artwork => {
          initialSearchTerms[artwork.vc_id] = '';
        });
        setGallerySearchTerms(initialSearchTerms);
      } catch (err) {
        setError('Failed to fetch artist data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (artistId) {
      loadArtistData();
    }
  }, [artistId]);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Not for Sale';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const handleGalleryChange = async (artworkId, newGalleryId) => {
    try {
      setUpdatingLocation(artworkId);
      
      await updateArtworkLocation(artworkId, newGalleryId);
      
      // Update local state
      setArtworks(prev => prev.map(artwork => 
        artwork.vc_id === artworkId 
          ? { 
              ...artwork, 
              location: newGalleryId,
              gallery: newGalleryId ? galleries.find(g => g.gallery_id === newGalleryId) : null
            }
          : artwork
      ));
      
      // Clear search term for this artwork
      setGallerySearchTerms(prev => ({
        ...prev,
        [artworkId]: ''
      }));
      
    } catch (error) {
      console.error('Error updating artwork location:', error);
      setError('Failed to update artwork location.');
    } finally {
      setUpdatingLocation(null);
    }
  };

  const getGalleryDisplayName = (artwork) => {
    if (artwork.gallery) {
      return `${artwork.gallery.name}${artwork.gallery.address ? ` - ${artwork.gallery.address}` : ''}`;
    }
    return 'Not in a gallery';
  };

  const getFilteredGalleries = (artworkId) => {
    const searchTerm = gallerySearchTerms[artworkId] || '';
    if (!searchTerm.trim()) {
      return galleries;
    }
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return galleries.filter(gallery => 
      gallery.name.toLowerCase().includes(lowerSearchTerm) ||
      (gallery.address && gallery.address.toLowerCase().includes(lowerSearchTerm)) ||
      (gallery.type && gallery.type.toLowerCase().includes(lowerSearchTerm))
    );
  };

  const handleGallerySearchChange = (artworkId, value) => {
    setGallerySearchTerms(prev => ({
      ...prev,
      [artworkId]: value
    }));
  };

  if (loading) {
    return <div className="loading">Loading Artist Details...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!artist) {
    return <div className="error">Artist not found.</div>;
  }

  return (
    <div className="artist-detail-container">
      <SEO 
        title={`${artist.full_name || artist.first_name} - Artist Profile | VibrantCast`}
        description={`Discover the amazing artwork by ${artist.full_name || artist.first_name}. Explore their portfolio, biography, and connect with this talented artist on VibrantCast.`}
        image={artist.profile_image || artist.image_url || '/images/logo-120.png'}
        keywords={`${artist.full_name}, artist, artwork, portfolio, ${artist.style || ''}, ${artist.medium || ''}, art, VibrantCast`}
        author={artist.full_name || artist.first_name}
        type="profile"
      />
      
      <Link to="/admin/artists" className="view-link" style={{ marginBottom: '2rem', display: 'inline-block' }}>
        &larr; Back to Dashboard
      </Link>
      <header className="artist-detail-header">
        <img
          src={artist.profile_img || `https://ui-avatars.com/api/?name=${artist.nickname}&background=random`}
          alt={artist.nickname}
          className="artist-detail-img"
        />
        <div className="artist-detail-info">
          <h1>{artist.nickname || `${artist.first_name} ${artist.last_name}`}</h1>
          <p className="handle">@{artist.handle || 'N/A'}</p>
          <p className="email">{artist.email}</p>
        </div>
      </header>

      <main>
        <h2>Artworks ({artworks.length})</h2>
        <div className="artist-artworks-grid">
          {artworks.map(artwork => (
            <div key={artwork.vc_id} className="artwork-card">
              <img 
                src={artwork.cover_image || 'https://placehold.co/600x400/EEE/31343C?text=No+Image'} 
                alt={artwork.title}
                className="artwork-card-img" 
              />
              <div className="artwork-card-info">
                <h3>{artwork.title}</h3>
                <p className="price">{formatPrice(artwork.price)}</p>
                <span className={`status status-${artwork.status?.toLowerCase()}`}>{artwork.status}</span>
                
                {/* Gallery Location Section */}
                <div className="gallery-location">
                  <label htmlFor={`gallery-${artwork.vc_id}`} className="gallery-label">
                    Gallery Location:
                  </label>
                  
                  {/* Searchable Gallery Dropdown */}
                  <div className="gallery-search-container">
                    <input
                      type="text"
                      placeholder="Search galleries..."
                      value={gallerySearchTerms[artwork.vc_id] || ''}
                      onChange={(e) => handleGallerySearchChange(artwork.vc_id, e.target.value)}
                      className="gallery-search-input"
                    />
                    <select
                      id={`gallery-${artwork.vc_id}`}
                      value={artwork.location || ''}
                      onChange={(e) => handleGalleryChange(artwork.vc_id, e.target.value ? parseInt(e.target.value) : null)}
                      disabled={updatingLocation === artwork.vc_id}
                      className="gallery-select"
                    >
                      <option value="">Not in a gallery</option>
                      {getFilteredGalleries(artwork.vc_id).map(gallery => (
                        <option key={gallery.gallery_id} value={gallery.gallery_id}>
                          {gallery.name}{gallery.address ? ` - ${gallery.address}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {updatingLocation === artwork.vc_id && (
                    <span className="updating-indicator">Updating...</span>
                  )}
                  <div className="current-location">
                    Current: {getGalleryDisplayName(artwork)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {artworks.length === 0 && (
          <p className="loading">This artist has no artworks.</p>
        )}
      </main>
    </div>
  );
};

export default ArtistDetailPage; 